import { observer } from 'mobx-react-lite'
import React, { useEffect, useReducer, useState } from 'react'
import styled from 'styled-components'
import { PrimaryButton, SecondaryButton } from '../../components/Button'
import { TextInputWithLabel } from '../../components/Input'
import { ProfilePicture } from '../../components/ProfilePicture'
import Radom, { IOrganization } from '../../state/Radom'
import Web3 from '../../state/User'
import { RADOM_COLORS } from '../../util/Constants'
import Spinner from '../../components/Spinner'
import { isEqual, pickBy } from 'lodash'
import AppearTransition from '../../components/AppearTransition'
import { errorToast, successToast } from '../../util/Util'
import Switch from '../../components/Switch'
import {
  SettingsCard,
  SettingsCardBody,
  SettingsCardDescription,
  SettingsCardHeader,
  SettingsCardTitle,
  SettingsEyebrow,
  SettingsGrid,
  SettingsHero,
  SettingsPage,
  SettingsRow,
  SettingsRowDescription,
  SettingsRows,
  SettingsRowTitle,
  SettingsSubtext,
  SettingsTitle,
  StatusPill
} from './SettingsLayout'

export const RADOM_TERMINAL_ENABLED_KEY = 'RADOM_TERMINAL_ENABLED'

const NameInput = styled.input<{ $isEditing: boolean }>`
  background: transparent;
  width: 100%;
  border: none;
  box-shadow: none;
  color: #20242d;
  font-size: 24px;
  font-weight: 500;
  border-radius: 0;
  padding: 0 0 4px;
  outline: none;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: all 0.2s ease-in-out;
  border-bottom: 1px solid ${({ $isEditing }) => $isEditing ? RADOM_COLORS.GRAY_DARK : 'transparent'};

  :focus {
    border-color: ${RADOM_COLORS.ORANGE};
  }
`

const ProfileSummary = styled.div`
  align-items: center;
  display: grid;
  gap: 16px;
  grid-template-columns: auto minmax(0, 1fr);
  min-width: 0;
`

const ProfileIdentity = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`

const ProfileActions = styled.div`
  align-items: center;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 2px;
`

const Profile = observer(() => {
  const emptySellerProfile: any = {
    organizationId: '',
    name: '',
    logoUrl: '',
    website: '',
    telegram: '',
    discord: '',
    twitter: '',
    imageBytes: undefined,
    defaultTokens: [],
    defaultChains: []
  }

  const [profile, updateProfile] = useReducer((prev, next) => {
    const parsedNext = Object.fromEntries(Object.entries(next).filter(([_, v]) => v != null))
    return { ...prev, ...parsedNext }
  }, emptySellerProfile)
  const [isLoading, setIsLoading] = useState(true)
  const [isEditing, setIsEditing] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [profileSnapshot, setProfileSnapshot] = useState<IOrganization>(profile)
  const [profileChanges, setProfileChanges] = useState<Partial<IOrganization>>({})
  const [imgUrl, setImgUrl] = useState<string | undefined>()
  const [imgFile, setImgFile] = useState<File | undefined>()

  const [isTerminalModeEnabled, setIsTerminalModeEnabled] = useState(false)

  useEffect(() => {
    setIsTerminalModeEnabled(!!localStorage.getItem(RADOM_TERMINAL_ENABLED_KEY))
  }, [])

  const onUpdateProfile = async (): Promise<void> => {
    if (isUpdating) return
    setIsUpdating(true)
    try {
      await Radom.updateSellerProfile({ organizationId: profile.organizationId, ...profileChanges })
      Radom.getSellerProfile()
      setProfileSnapshot(profile)
      setIsEditing(false)
      successToast('Successfully updated seller profile')
    } catch (err) {
      console.error('Failed to create seller profile', err)
      errorToast(err.response?.data?.error || err.message)
    }
    setIsUpdating(false)
  }

  const onEdit = (): void => {
    setIsEditing(true)
  }

  const onCancelEdit = (): void => {
    setIsEditing(false)
    updateProfile(profileSnapshot)
  }

  useEffect(() => {
    Web3.connectedAccount
      .then(() => {
        Radom.getSellerProfile()
          .then(p => {
            updateProfile(p)
            setProfileSnapshot(p)
            setIsEditing(false)
            if (p.logoUrl) {
              setImgUrl(p.logoUrl)
            }
          })
          .catch(_ => {
            console.error('Failed')
            const emptyProfile = {
              sellerAddress: Web3.accounts[0]
            }
            updateProfile(emptyProfile)
            setProfileSnapshot({ ...profile, ...emptyProfile })
          })
          .finally(() => setIsLoading(false))
      })
  }, [])

  useEffect(() => {
    const changes = pickBy(profile, (v, k) => {
      return k === 'sellerAddress' ||
        k === 'defaultChains' || k === 'defaultTokens' ||
        (!isEqual(profileSnapshot[k], v))
    })
    setProfileChanges({ ...changes })
  }, [profile])

  useEffect(() => {
    if (imgFile) {
      setImgUrl(URL.createObjectURL(imgFile))
      imgFile.arrayBuffer()
        .then(b => {
          updateProfile({
            imageBytes: Array.from(new Uint8Array(b))
          })
        })
    }
  }, [imgFile])

  if (isLoading) {
    return <Spinner />
  }

  const isProfileComplete = Boolean(profile.name && profile.website)

  return <SettingsPage>
    <SettingsHero>
      <div>
        <SettingsEyebrow>Organization settings</SettingsEyebrow>
        <SettingsTitle>Branding</SettingsTitle>
        <SettingsSubtext>
          Manage the seller profile shown across hosted checkout, invoices, and payment links.
        </SettingsSubtext>
      </div>
      <AppearTransition
        in={!isEditing}
        duration={200}
        transitionId="profile-edit">
        <SecondaryButton onClick={onEdit}>Edit branding</SecondaryButton>
      </AppearTransition>
    </SettingsHero>

    <SettingsCard>
      <SettingsCardHeader>
        <ProfileSummary>
          <ProfilePicture
            imgUrl={imgUrl}
            imgFile={imgFile}
            setImgFile={setImgFile}
            isEditing={isEditing}
          />
          <ProfileIdentity>
            <SettingsCardTitle>Seller profile</SettingsCardTitle>
            <NameInput
              value={profile.name}
              disabled={!isEditing}
              placeholder={profile.sellerAddress || 'Merchant name'}
              onChange={(e) => updateProfile({ name: (e.target as HTMLInputElement).value })}
              $isEditing={isEditing}
            />
            <SettingsCardDescription>
              This identity appears on customer-facing payment pages and receipts.
            </SettingsCardDescription>
          </ProfileIdentity>
        </ProfileSummary>
        <StatusPill $tone={isProfileComplete ? 'green' : 'amber'}>
          {isProfileComplete ? 'Complete' : 'Needs attention'}
        </StatusPill>
      </SettingsCardHeader>

      <SettingsCardBody>
        <SettingsGrid>
          <TextInputWithLabel
            label="Website URL"
            value={profile.website}
            disabled={!isEditing}
            placeholder="https://radom.com/"
            onChange={(e) => updateProfile({ website: (e.target as HTMLInputElement).value })}
          />
          <TextInputWithLabel
            label="Telegram"
            value={profile.telegram}
            disabled={!isEditing}
            placeholder="@radomnetwork"
            onChange={(e) => updateProfile({ telegram: (e.target as HTMLInputElement).value })}
          />
          <TextInputWithLabel
            label="Discord"
            value={profile.discord}
            disabled={!isEditing}
            placeholder="https://discord.gg/invite/aX3Rtv"
            onChange={(e) => updateProfile({ discord: (e.target as HTMLInputElement).value })}
          />
          <TextInputWithLabel
            label="Twitter"
            value={profile.twitter}
            disabled={!isEditing}
            placeholder="@radomnetwork"
            onChange={(e) => updateProfile({ twitter: (e.target as HTMLInputElement).value })}
          />
        </SettingsGrid>
      </SettingsCardBody>
    </SettingsCard>

    <SettingsCard>
      <SettingsCardHeader>
        <div>
          <SettingsCardTitle>Terminal mode</SettingsCardTitle>
          <SettingsCardDescription>
            Enable the checkout terminal controls used by the hosted checkout session workflow.
          </SettingsCardDescription>
        </div>
      </SettingsCardHeader>
      <SettingsCardBody>
        <SettingsRows>
          <SettingsRow>
            <div>
              <SettingsRowTitle>Terminal checkout controls</SettingsRowTitle>
              <SettingsRowDescription>
                This affects the dashboard session on this browser and is mostly used for in-person checkout flows.
              </SettingsRowDescription>
            </div>
            <Switch
              disabled={!isEditing}
              checked={isTerminalModeEnabled}
              onClick={() => {
                if (localStorage.getItem(RADOM_TERMINAL_ENABLED_KEY)) {
                  localStorage.removeItem(RADOM_TERMINAL_ENABLED_KEY)
                  setIsTerminalModeEnabled(false)
                } else {
                  localStorage.setItem(RADOM_TERMINAL_ENABLED_KEY, 'ENABLED')
                  setIsTerminalModeEnabled(true)
                }
              }} />
          </SettingsRow>
        </SettingsRows>
      </SettingsCardBody>
    </SettingsCard>

    <AppearTransition
      in={isEditing}
      duration={200}
      transitionId="update-cancel">
      <ProfileActions>
        <SecondaryButton
          style={{ color: RADOM_COLORS.GRAY_DARKER }}
          onClick={onCancelEdit}>
          Cancel
        </SecondaryButton>
        <PrimaryButton
          isLoading={isUpdating}
          style={{ minWidth: 220 }}
          disabled={Object.keys(profileChanges).length === 0}
          onClick={onUpdateProfile}>
          Save changes
        </PrimaryButton>
      </ProfileActions>
    </AppearTransition>
  </SettingsPage>
})

export default Profile
